import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, X, Clock, AlertTriangle, Calendar, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPO_CONFIG = {
  falta: { icon: X, color: 'text-red-500', bg: 'bg-red-100' },
  atraso: { icon: Clock, color: 'text-orange-500', bg: 'bg-orange-100' },
  marcacao_incompleta: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-100' },
  hora_extra_nao_autorizada: { icon: Clock, color: 'text-blue-500', bg: 'bg-blue-100' },
  interjornada_insuficiente: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-100' },
  intrajornada_insuficiente: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-100' },
  jornada_excedida: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-100' },
  justificativa_aprovada: { icon: Check, color: 'text-green-500', bg: 'bg-green-100' },
  justificativa_rejeitada: { icon: X, color: 'text-red-500', bg: 'bg-red-100' },
  alerta_geral: { icon: Bell, color: 'text-slate-500', bg: 'bg-slate-100' }
};

const UnifiedNotificacoes = ({ token }) => {
  const [open, setOpen] = useState(false);
  const [notificacoes, setNotificacoes] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchContador();
    const interval = setInterval(fetchContador, 60000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (open) fetchNotificacoes();
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchContador = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/notificacoes/contador`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNaoLidas(data.nao_lidas || 0);
      }
    } catch (err) {
      console.error('Erro:', err);
    }
  };

  const fetchNotificacoes = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/notificacoes?limite=15`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotificacoes(data.notificacoes || []);
        setNaoLidas(data.total_nao_lidas || 0);
      }
    } catch (err) {
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  };

  const marcarComoLida = async (id) => {
    try {
      await fetch(`${API_URL}/api/notificacoes/${id}/marcar-lida`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNotificacoes(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n));
      setNaoLidas(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Erro:', err);
    }
  };

  const formatarTempo = (dataStr) => {
    if (!dataStr) return '';
    const data = new Date(dataStr);
    const agora = new Date();
    const diff = agora - data;
    const minutos = Math.floor(diff / 60000);
    const horas = Math.floor(diff / 3600000);
    const dias = Math.floor(diff / 86400000);

    if (minutos < 1) return 'Agora';
    if (minutos < 60) return `${minutos}min`;
    if (horas < 24) return `${horas}h`;
    if (dias < 7) return `${dias}d`;
    return data.toLocaleDateString('pt-BR');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-white/80 hover:text-white transition-colors"
        data-testid="notificacoes-unified-btn"
      >
        <Bell className="w-6 h-6" />
        {naoLidas > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {naoLidas > 9 ? '9+' : naoLidas}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl overflow-hidden z-50">
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3">
            <h3 className="font-semibold text-white">Notificações</h3>
            <p className="text-xs text-slate-300">{naoLidas} nova(s)</p>
          </div>

          {/* Lista */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-slate-400">
                <div className="animate-spin w-6 h-6 border-2 border-slate-300 border-t-emerald-500 rounded-full mx-auto"></div>
              </div>
            ) : notificacoes.length === 0 ? (
              <div className="p-6 text-center text-slate-400">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Sem notificações</p>
              </div>
            ) : (
              notificacoes.map(n => {
                const config = TIPO_CONFIG[n.tipo] || TIPO_CONFIG.alerta_geral;
                const Icon = config.icon;
                return (
                  <div 
                    key={n.id}
                    onClick={() => !n.lida && marcarComoLida(n.id)}
                    className={`p-3 border-b border-slate-100 ${n.lida ? 'opacity-50' : 'bg-blue-50/30'} hover:bg-slate-50 cursor-pointer`}
                  >
                    <div className="flex gap-3">
                      <div className={`p-2 rounded-full ${config.bg} shrink-0`}>
                        <Icon className={`w-4 h-4 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm text-slate-800 ${n.lida ? '' : 'font-semibold'}`}>
                          {n.titulo}
                        </p>
                        <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{n.mensagem}</p>
                        <span className="text-xs text-slate-400 mt-1 block">{formatarTempo(n.created_at)}</span>
                      </div>
                      {!n.lida && <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0 mt-1.5"></span>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedNotificacoes;
